import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { HomeIcon, UsersIcon, BuildingIcon, LayersIcon, UploadIcon, BarChartIcon, ChevronDownIcon, ChevronRightIcon, LogOutIcon, AlertTriangleIcon, TagIcon, GiftIcon, ShieldAlertIcon } from 'lucide-react';
interface SidebarProps {
  isOpen: boolean;
}
interface NavItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  children?: Array<{
    name: string;
    path: string;
  }>;
}
const Sidebar: React.FC<SidebarProps> = ({
  isOpen
}) => {
  const {
    theme
  } = useTheme();
  const {
    logout,
    user
  } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    'User & Role Management': true,
    'Merchant Onboarding': false,
    'Aggregator & Super Merchant': false,
    'Till Management': false,
    'Bulk Operations': false,
    'Campaign Management': false,
    'Cashback Incentives': true,
    'Dispute Management': false,
    'Transaction Rules Management': false,
    Reports: false
  });
  const toggleExpand = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };
  const navItems: NavItem[] = [{
    name: 'Dashboard',
    icon: <HomeIcon className="h-5 w-5" data-id="element-423" />,
    path: '/dashboard'
  }, {
    name: 'User & Role Management',
    icon: <UsersIcon className="h-5 w-5" data-id="element-424" />,
    children: [{
      name: 'User List',
      path: '/users'
    }, {
      name: 'Role Management',
      path: '/roles'
    }, {
      name: 'Audit Logs',
      path: '/audit-logs'
    }, {
      name: 'Session History',
      path: '/session-history'
    }]
  }, {
    name: 'Merchant Onboarding',
    icon: <BuildingIcon className="h-5 w-5" data-id="element-425" />,
    children: [{
      name: 'Merchant List',
      path: '/merchants'
    }, {
      name: 'New Merchant',
      path: '/merchants/new'
    }]
  }, {
    name: 'Aggregator & Super Merchant',
    icon: <LayersIcon className="h-5 w-5" data-id="element-426" />,
    children: [{
      name: 'Aggregator Management',
      path: '/aggregators'
    }, {
      name: 'Chain Merchant Setup',
      path: '/chain-merchants'
    }, {
      name: 'Hierarchy View',
      path: '/hierarchy'
    }]
  }, {
    name: 'Till Management',
    icon: <LayersIcon className="h-5 w-5" data-id="element-427" />,
    children: [{
      name: 'Till List',
      path: '/tills'
    }, {
      name: 'New Till',
      path: '/tills/new'
    }]
  }, {
    name: 'Bulk Operations',
    icon: <UploadIcon className="h-5 w-5" data-id="element-428" />,
    children: [{
      name: 'Bulk QR Generation',
      path: '/bulk-qr'
    }, {
      name: 'Bulk Merchant Upload',
      path: '/bulk-merchant'
    }]
  }, {
    name: 'Campaign Management',
    icon: <TagIcon className="h-5 w-5" data-id="element-429" />,
    children: [{
      name: 'Campaign Dashboard',
      path: '/campaigns/dashboard'
    }, {
      name: 'All Campaigns',
      path: '/campaigns'
    }, {
      name: 'Create Campaign',
      path: '/campaigns/new'
    }]
  }, {
    name: 'Cashback Incentives',
    icon: <GiftIcon className="h-5 w-5" data-id="element-430" />,
    children: [{
      name: 'Dashboard',
      path: '/cashback/dashboard'
    }, {
      name: 'Campaigns',
      path: '/cashback/campaigns'
    }, {
      name: 'Approvals',
      path: '/cashback/approvals'
    }, {
      name: 'Settlements',
      path: '/cashback/settlements'
    }, {
      name: 'Reports',
      path: '/cashback/reports'
    }]
  }, {
    name: 'Dispute Management',
    icon: <ShieldAlertIcon className="h-5 w-5" data-id="element-431" />,
    children: [{
      name: 'Dashboard',
      path: '/disputes/dashboard'
    }, {
      name: 'All Disputes',
      path: '/disputes'
    }, {
      name: 'Bulk Upload',
      path: '/disputes/bulk-upload'
    }, {
      name: 'Scheme Ingestion',
      path: '/disputes/scheme-upload'
    }, {
      name: 'Scheme Export',
      path: '/disputes/scheme-export'
    }, {
      name: 'Chargebacks',
      path: '/disputes/chargebacks'
    }, {
      name: 'Reports & Analytics',
      path: '/disputes/reports'
    }, {
      name: 'Template Config',
      path: '/disputes/scheme-config'
    }]
  }, {
    name: 'Transaction Rules Management',
    icon: <AlertTriangleIcon className="h-5 w-5" data-id="element-432" />,
    children: [{
      name: 'Dashboard Overview',
      path: '/transaction-rules/dashboard'
    }, {
      name: 'Rules Management',
      path: '/transaction-rules/rules'
    }, {
      name: 'Risk Rule Alerts',
      path: '/transaction-rules/alerts'
    }, {
      name: 'Transaction Monitoring',
      path: '/transaction-rules/monitoring'
    }, {
      name: 'Audit & Logs',
      path: '/transaction-rules/audit-logs'
    }]
  }, {
    name: 'Reports',
    icon: <BarChartIcon className="h-5 w-5" data-id="element-433" />,
    children: [{
      name: 'Transaction Detail',
      path: '/reports/transaction-detail'
    }, {
      name: 'Transaction Summary',
      path: '/reports/transaction-summary'
    }, {
      name: 'Pending Payments',
      path: '/reports/pending-payments'
    }, {
      name: 'Failed Payments',
      path: '/reports/failed-payments'
    }]
  }];
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  return <aside className={`${isOpen ? 'w-64' : 'w-20'} transition-all duration-300 ease-in-out ${theme === 'dark' ? 'bg-slate-900 text-slate-300 border-slate-800' : 'bg-white text-slate-600 border-slate-200'} border-r flex flex-col z-40 shadow-sm`} data-id="element-434">
      <div className={`h-16 flex items-center border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'} ${isOpen ? 'px-4 justify-start' : 'justify-center'}`} data-id="element-435">
        {isOpen ? <div className="flex items-center space-x-2" data-id="element-436">
            <img src="https://cdn.magicpatterns.com/uploads/or2hjL7AE4KDb5QoZCHTjR/Paysys_Logo.png" alt="Paysys" className="h-8 object-contain" data-id="element-437" />
          </div> : <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center shadow-sm" data-id="element-438">
            <span className="text-white font-bold text-lg leading-none" data-id="element-439">P</span>
          </div>}
      </div>

      <nav className="flex-1 overflow-y-auto py-6 custom-scrollbar" data-id="element-440">
        <ul className="space-y-1.5 px-3" data-id="element-441">
          {navItems.map(item => <li key={item.name} data-id="element-442">
              {item.children ? <div data-id="element-443">
                  <button onClick={() => toggleExpand(item.name)} className={`flex items-center w-full p-2.5 rounded-lg transition-all duration-200 group ${expandedItems[item.name] ? 'bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white font-medium' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-id="element-444">
                    <span className={`mr-3 transition-colors ${expandedItems[item.name] ? 'text-accent-500' : 'text-slate-400 group-hover:text-slate-500 dark:group-hover:text-slate-300'}`} data-id="element-445">
                      {item.icon}
                    </span>
                    {isOpen && <>
                        <span className="flex-1 text-left text-sm" data-id="element-446">
                          {item.name}
                        </span>
                        {expandedItems[item.name] ? <ChevronDownIcon className="h-4 w-4 text-slate-400" data-id="element-447" /> : <ChevronRightIcon className="h-4 w-4 text-slate-400" data-id="element-448" />}
                      </>}
                  </button>
                  {isOpen && expandedItems[item.name] && <ul className="mt-1 mb-2 space-y-1 relative before:absolute before:inset-y-0 before:left-[21px] before:w-px before:bg-slate-200 dark:before:bg-slate-700" data-id="element-449">
                      {item.children.map(child => {
                const active = isActive(child.path);
                return <li key={child.name} className="relative" data-id="element-450">
                            <Link to={child.path} className={`block py-2 pl-11 pr-3 rounded-lg text-sm transition-all duration-200 ${active ? 'text-accent-600 dark:text-accent-400 font-medium bg-accent-50/50 dark:bg-accent-900/10' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-id="element-451">
                              {active && <span className="absolute left-[20px] top-1/2 -translate-y-1/2 w-1 h-4 bg-accent-500 rounded-full" data-id="element-452"></span>}
                              {child.name}
                            </Link>
                          </li>;
              })}
                    </ul>}
                </div> : <Link to={item.path || '#'} className={`flex items-center p-2.5 rounded-lg transition-all duration-200 group relative overflow-hidden ${isActive(item.path || '') ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300 font-medium' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-white'}`} data-id="element-453">
                  {isActive(item.path || '') && <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-accent-500 to-violet-500 rounded-r-full" data-id="element-454"></span>}
                  <span className={`mr-3 transition-colors ${isActive(item.path || '') ? 'text-accent-600 dark:text-accent-400' : 'text-slate-400 group-hover:text-slate-500 dark:group-hover:text-slate-300'}`} data-id="element-455">
                    {item.icon}
                  </span>
                  {isOpen && <span className="text-sm" data-id="element-456">{item.name}</span>}
                </Link>}
            </li>)}
        </ul>
      </nav>

      <div className={`p-4 border-t ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`} data-id="element-457">
        <button onClick={logout} className={`flex items-center w-full p-2.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200 text-slate-500 dark:text-slate-400 group ${!isOpen && 'justify-center'}`} data-id="element-458">
          <LogOutIcon className={`h-5 w-5 ${isOpen && 'mr-3'} group-hover:text-red-500 transition-colors`} data-id="element-459" />
          {isOpen && <span className="text-sm font-medium" data-id="element-460">Sign Out</span>}
        </button>
      </div>
    </aside>;
};
export default Sidebar;